import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Search, Phone, Mail, MapPin, Building2, Home, Star, Crown, Loader2, X,
  ArrowLeft, Briefcase, FileText, StickyNote, ChevronRight, Send, Navigation,
  Activity as ActivityIcon, Image as ImageIcon, ClipboardCheck, History as HistoryIcon,
  Wrench, HelpCircle, Type as TypeIcon, Trash2, Receipt, CircleDollarSign, Pencil,
  Check, User, CheckSquare, MessageCircle, MoreHorizontal, LayoutGrid, Snowflake,
  DollarSign, Calendar, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { customersApi, jobsApi, estimatesApi, invoicesApi, fileUrl } from "../../lib/api";
import { customerStatusColors } from "../../data/customerMock";
import { useAuth } from "../../contexts/AuthContext";
import { PhotoCaptureButton } from "../../components/PhotoCaptureButton";
import NewCustomerModal from "../../components/NewCustomerModal";
import NewJobModal from "../../components/NewJobModal";
import MobileBottomNav from "../../components/mobile/MobileBottomNav";
import EmailDocModal from "../../components/EmailDocModal";
import { CreateDialog } from "../Estimates";

const CUSTOMER_HEADER_BG = process.env.REACT_APP_BACKEND_URL + "/api/assets/mobile-customer-header-bg";
const CUSTOMER_HEADER_MASCOT = process.env.REACT_APP_BACKEND_URL + "/api/assets/dashboard-welcome-mascot";

const TYPE_FILTERS = ["All", "Residential", "Commercial"];

/**
 * Mobile Customers — searchable list optimized for one-handed use.
 *
 * • Sticky search bar + type chips at the top
 * • Compact rows with icon avatar, name, VIP/Member badges, and address
 * • Tap-to-call and tap-to-email inline actions
 * • Tap the row → opens a full-screen customer-file sheet (?id=…) with
 *   full feature parity to the desktop Customer detail (Overview, Activity,
 *   Jobs, Photos, Billing, Check List, Service History, Properties,
 *   Equipment, Notes).
 */
export default function MobileCustomers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("All");

  const activeId = searchParams.get("id") || null;

  useEffect(() => {
    let cancelled = false;
    customersApi.list()
      .then((cs) => { if (!cancelled) setCustomers(cs || []); })
      .catch(() => { if (!cancelled) setCustomers([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return customers
      .filter((c) => (type === "All" ? true : c.type === type))
      .filter((c) => {
        if (!needle) return true;
        const blob = `${c.name || ""} ${c.email || ""} ${c.phone || ""} ${c.address || ""} ${c.city || ""}`.toLowerCase();
        return blob.includes(needle);
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [customers, q, type]);

  const openCustomer = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("id", id);
    setSearchParams(next);
  };
  const closeCustomer = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("id");
    setSearchParams(next);
  };

  if (activeId) {
    return <MobileCustomerDetail customerId={activeId} onBack={closeCustomer} />;
  }

  return (
    <div
      className="mv md:hidden min-h-screen w-full"
      style={{
        color: "#0f172a",
        paddingTop: "calc(3.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(5rem + env(safe-area-inset-bottom))",
      }}
      data-testid="mobile-customers"
    >
      {/* Search + type chips */}
      <div className="px-4 pt-3 pb-2 bg-white border-b border-slate-200">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customers…"
            data-testid="mobile-customers-search"
            className="w-full h-11 pl-9 pr-9 rounded-full border border-slate-200 text-[14px] font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            style={{ backgroundColor: "#F1F5F9", color: "#0f172a" }}
          />
          {q && (
            <button
              onClick={() => setQ("")}
              data-testid="mobile-customers-clear-search"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full flex items-center justify-center text-slate-400 active:bg-slate-100"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="mt-2 flex gap-1.5 overflow-x-auto no-scrollbar" data-testid="mobile-customers-type-chips">
          {TYPE_FILTERS.map((t) => (
            <button
              key={t}
              onClick={() => setType(t)}
              data-testid={`mobile-customers-chip-${t.toLowerCase()}`}
              className={`px-3 h-8 rounded-full text-[12px] font-semibold whitespace-nowrap transition-colors ${
                type === t ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="p-10 flex items-center justify-center text-slate-400 text-sm" data-testid="mobile-customers-loading">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading customers…
        </div>
      ) : visible.length === 0 ? (
        <div className="p-10 text-center text-slate-500 text-sm" data-testid="mobile-customers-empty">
          {q || type !== "All" ? "No customers match those filters." : "No customers yet."}
        </div>
      ) : (
        <>
          <div className="px-4 pt-3 text-[11px] font-bold uppercase tracking-wider text-slate-500" data-testid="mobile-customers-count">
            {visible.length} customer{visible.length === 1 ? "" : "s"}
          </div>
          <ul className="mt-2 divide-y divide-slate-100 bg-white" data-testid="mobile-customers-list">
            {visible.map((c) => (
              <CustomerRow key={c.id} c={c} onOpen={openCustomer} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function CustomerRow({ c, onOpen }) {
  const isCommercial = c.type === "Commercial";
  const isVip = Array.isArray(c.tags) && c.tags.includes("VIP");
  const isMember = c.membershipInfo && c.membershipInfo.status === "active";
  const cityLine = [c.city, c.state].filter(Boolean).join(", ");

  return (
    <li>
      <button
        onClick={() => onOpen(c.id)}
        data-testid={`mobile-customers-row-${c.id}`}
        className="w-full text-left px-4 py-3 flex items-start gap-3 active:bg-slate-50"
      >
        <div
          className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${
            isCommercial ? "bg-violet-100" : "bg-blue-100"
          }`}
        >
          {isCommercial ? (
            <Building2 className="h-5 w-5" color="#7C3AED" />
          ) : (
            <Home className="h-5 w-5" color="#2563EB" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <div className="text-[15px] font-bold truncate" style={{ color: "#0f172a" }}>{c.name || "Unnamed"}</div>
            {isVip && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400 shrink-0" />}
            {isMember && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5">
                <Crown className="h-3 w-3" /> {c.membershipInfo.planName || "Member"}
              </span>
            )}
          </div>
          {c.address && (
            <div className="mt-0.5 flex items-start gap-1.5 text-[12.5px] text-slate-500">
              <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span className="truncate">{c.address}{cityLine ? ` · ${cityLine}` : ""}</span>
            </div>
          )}
          <div className="mt-1.5 flex items-center gap-3 text-[12px]">
            {c.phone && (
              <a
                href={`tel:${c.phone.replace(/[^0-9]/g, "")}`}
                data-testid={`mobile-customers-call-${c.id}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 font-semibold text-blue-600"
              >
                <Phone className="h-3.5 w-3.5" /> Call
              </a>
            )}
            {c.email && (
              <a
                href={`mailto:${c.email}`}
                data-testid={`mobile-customers-email-${c.id}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 font-semibold text-blue-600"
              >
                <Mail className="h-3.5 w-3.5" /> Email
              </a>
            )}
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-slate-300 shrink-0 mt-1" />
      </button>
    </li>
  );
}

const PRIMARY_TABS = [
  { k: "overview", label: "Overview", icon: Home },
  { k: "jobs", label: "Jobs", icon: Briefcase },
  { k: "estimates", label: "Estimates", icon: FileText },
  { k: "billing", label: "Billing", icon: DollarSign },
];
const MORE_TABS = [
  { k: "activity", label: "Activity", icon: ActivityIcon },
  { k: "photos", label: "Photos", icon: ImageIcon },
  { k: "checklist", label: "Check List", icon: ClipboardCheck },
  { k: "history", label: "History", icon: HistoryIcon },
  { k: "properties", label: "Properties", icon: Building2 },
  { k: "equipment", label: "Equipment", icon: Wrench },
  { k: "notes", label: "Notes", icon: StickyNote },
  { k: "payments", label: "Payments", icon: CircleDollarSign },
];
const JOB_ICON_STYLES = [
  { bg: "bg-blue-100", color: "#2563EB", icon: Wrench },
  { bg: "bg-emerald-100", color: "#10B981", icon: Wrench },
  { bg: "bg-violet-100", color: "#7C3AED", icon: Calendar },
];

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

// Same "Mon, Jul 7, 2025 · 10:30 AM" formatter used on the desktop Customer page.
function formatStamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return `${date} · ${time}`;
}

const ACTIVITY_META = {
  form_submitted:    { color: "bg-blue-100 text-blue-600",       Icon: FileText },
  customer_created:  { color: "bg-emerald-100 text-emerald-600", Icon: User },
  pipeline_added:    { color: "bg-violet-100 text-violet-600",   Icon: Briefcase },
  assigned:          { color: "bg-amber-100 text-amber-600",     Icon: User },
  stage_moved:       { color: "bg-indigo-100 text-indigo-600",   Icon: ChevronRight },
  job_created:       { color: "bg-blue-100 text-blue-700",       Icon: Briefcase },
  job_status:        { color: "bg-indigo-100 text-indigo-700",   Icon: ActivityIcon },
  job_completed:     { color: "bg-emerald-100 text-emerald-700", Icon: CheckSquare },
  payment:           { color: "bg-emerald-100 text-emerald-700", Icon: CircleDollarSign },
  note:              { color: "bg-yellow-100 text-yellow-700",   Icon: StickyNote },
  estimate_created:  { color: "bg-sky-100 text-sky-700",         Icon: FileText },
  estimate_accepted: { color: "bg-emerald-100 text-emerald-700", Icon: CheckSquare },
  invoice_created:   { color: "bg-blue-100 text-blue-700",       Icon: Receipt },
  invoice_paid:      { color: "bg-emerald-100 text-emerald-700", Icon: CircleDollarSign },
};

const ANSWER_ICON = {
  question: HelpCircle,
  input: TypeIcon,
  action: Wrench,
  diagnosis: ActivityIcon,
  recommend: Receipt,
};

function MobileCustomerDetail({ customerId, onBack }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "dispatcher";
  const isAdmin = user?.role === "admin";
  const myEmail = user?.email || "";

  const [customer, setCustomer] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [newJobOpen, setNewJobOpen] = useState(false);
  const [estimateOpen, setEstimateOpen] = useState(false);

  const [noteText, setNoteText] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");

  const [photoBusy, setPhotoBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [emailTarget, setEmailTarget] = useState(null); // invoice being emailed a pay link from the Payments tab

  const load = useCallback(async () => {
    try {
      const [c, j, e, i] = await Promise.all([
        customersApi.get(customerId),
        jobsApi.list(),
        estimatesApi.list(),
        invoicesApi.list(),
      ]);
      setCustomer(c);
      setJobs((j || []).filter((x) => x.customerId === customerId));
      setEstimates((e || []).filter((x) => x.customerId === customerId));
      setInvoices((i || []).filter((x) => x.customerId === customerId));
    } catch {
      toast.error("Could not load that customer");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!noteText.trim() || noteBusy) return;
    setNoteBusy(true);
    try {
      const updated = await customersApi.addNote(customerId, noteText.trim());
      setCustomer(updated);
      setNoteText("");
      toast.success("Note added");
    } catch {
      toast.error("Could not add note");
    } finally {
      setNoteBusy(false);
    }
  };

  const beginEditNote = (n) => {
    setEditingNoteId(n.id);
    setEditingNoteText(n.text);
  };
  const cancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteText("");
  };
  const saveEditNote = async () => {
    const text = editingNoteText.trim();
    if (!text || !editingNoteId || noteBusy) return;
    setNoteBusy(true);
    try {
      const updated = await customersApi.updateNote(customerId, editingNoteId, text);
      setCustomer(updated);
      cancelEditNote();
    } catch {
      toast.error("Could not update note");
    } finally {
      setNoteBusy(false);
    }
  };
  const removeNote = async (noteId) => {
    if (noteBusy) return;
    if (!window.confirm("Delete this note? This cannot be undone.")) return;
    setNoteBusy(true);
    try {
      const updated = await customersApi.removeNote(customerId, noteId);
      setCustomer(updated);
      toast.success("Note deleted");
    } catch {
      toast.error("Could not delete note");
    } finally {
      setNoteBusy(false);
    }
  };

  const uploadPhotos = async (files) => {
    if (!files || !files.length || photoBusy) return;
    const count = files.length;
    setPhotoBusy(true);
    try {
      const updated = await customersApi.uploadPhotos(customerId, files);
      setCustomer(updated);
      toast.success(`${count} photo${count === 1 ? "" : "s"} added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed");
    } finally {
      setPhotoBusy(false);
    }
  };
  const deletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo? This cannot be undone.")) return;
    try {
      const updated = await customersApi.deletePhoto(customerId, photoId);
      setCustomer(updated);
      setLightbox(null);
      toast.success("Photo deleted");
    } catch {
      toast.error("Failed to delete photo");
    }
  };

  const deleteCustomer = async () => {
    setHeaderMenuOpen(false);
    if (!window.confirm("Delete this customer? This cannot be undone.")) return;
    try {
      await customersApi.remove(customerId);
      toast.success("Customer deleted");
      onBack();
    } catch {
      toast.error("Could not delete customer");
    }
  };

  // Same activity aggregation as the desktop Customer > Activity tab: lead
  // activity + job log events + invoice payments + notes + estimate/invoice milestones.
  const activityItems = useMemo(() => {
    if (!customer) return [];
    const out = [];
    for (const a of customer.activity || []) {
      out.push({ id: `act-${a.id}`, type: a.type, message: a.message, createdAt: a.createdAt, by: a.by });
    }
    for (const j of jobs) {
      for (const l of j.log || []) {
        if (!l.time) continue;
        const isCreated = l.type === "created";
        const isCompleted = l.type === "finished";
        out.push({
          id: `job-${j.id}-${l.id}`,
          type: isCreated ? "job_created" : (isCompleted ? "job_completed" : "job_status"),
          message: isCreated ? `Job ${j.id} created · ${j.type || "Service"}` : `${j.id} · ${l.label}`,
          createdAt: l.time,
          by: l.by,
          onClick: () => navigate(`/mobile/jobs/${j.id}`),
        });
      }
    }
    for (const inv of invoices) {
      for (const p of inv.payments || []) {
        out.push({
          id: `pay-${p.id}`,
          type: "payment",
          message: `Payment received · $${Number(p.amount || 0).toLocaleString()} via ${p.method}${p.reference ? ` (${p.reference})` : ""} on ${inv.id}`,
          createdAt: p.receivedAt,
          by: p.recordedBy,
          onClick: () => navigate(`/estimates?id=${inv.id}`),
        });
      }
    }
    for (const n of customer.notes || []) {
      const preview = (n.text || "").length > 100 ? `${n.text.slice(0, 100)}…` : n.text;
      out.push({ id: `note-${n.id}`, type: "note", message: `Note added · ${preview}`, createdAt: n.createdAt, by: n.createdBy, onClick: () => setTab("notes") });
    }
    for (const e of estimates) {
      if (e.issuedAt) out.push({ id: `est-${e.id}-issued`, type: "estimate_created", message: `Estimate ${e.id} created · $${Number(e.total || 0).toLocaleString()}`, createdAt: e.issuedAt, onClick: () => navigate(`/estimates?id=${e.id}`) });
      if (e.acceptedAt) out.push({ id: `est-${e.id}-accepted`, type: "estimate_accepted", message: `Estimate ${e.id} accepted`, createdAt: e.acceptedAt, onClick: () => navigate(`/estimates?id=${e.id}`) });
    }
    for (const inv of invoices) {
      if (inv.issuedAt) out.push({ id: `inv-${inv.id}-issued`, type: "invoice_created", message: `Invoice ${inv.id} created · $${Number(inv.total || 0).toLocaleString()}`, createdAt: inv.issuedAt, onClick: () => navigate(`/estimates?id=${inv.id}`) });
      if (inv.status === "Paid") {
        const lastPay = (inv.payments || []).slice().sort((a, b) => new Date(b.receivedAt) - new Date(a.receivedAt))[0];
        const paidAt = lastPay ? lastPay.receivedAt : inv.issuedAt;
        if (paidAt) out.push({ id: `inv-${inv.id}-paid`, type: "invoice_paid", message: `Invoice ${inv.id} paid in full`, createdAt: paidAt, onClick: () => navigate(`/estimates?id=${inv.id}`) });
      }
    }
    out.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return out;
  }, [customer, jobs, estimates, invoices, navigate]);

  // Payments tab: flatten every invoice's payments[] newest-first, each
  // carrying the job/estimate/invoice it's attached to for linking.
  const customerPayments = useMemo(() => {
    const out = [];
    for (const inv of invoices) {
      const estimate = inv.estimateId ? estimates.find((e) => e.id === inv.estimateId) : null;
      for (const p of inv.payments || []) {
        out.push({ id: p.id, invoiceId: inv.id, jobId: inv.jobId || null, estimate, amount: p.amount, receivedAt: p.receivedAt });
      }
    }
    return out.sort((a, b) => new Date(b.receivedAt) - new Date(a.receivedAt));
  }, [invoices, estimates]);

  // Open Balances: any invoice still owing money for this customer.
  const customerOpenBalances = useMemo(() => {
    return invoices
      .filter((inv) => (inv.balanceDue || 0) > 0 && inv.status !== "Voided")
      .map((inv) => ({
        invoiceId: inv.id,
        invoice: inv,
        jobId: inv.jobId || null,
        estimate: inv.estimateId ? estimates.find((e) => e.id === inv.estimateId) : null,
        balanceDue: inv.balanceDue,
      }))
      .sort((a, b) => (b.balanceDue || 0) - (a.balanceDue || 0));
  }, [invoices, estimates]);

  if (loading) {
    return (
      <div className="mv md:hidden fixed inset-0 z-[60] flex items-center justify-center" style={{ backgroundColor: "#F1F5F9" }} data-testid="mobile-customer-sheet-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        <MobileBottomNav />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="mv md:hidden fixed inset-0 z-[60] flex flex-col items-center justify-center gap-3" style={{ backgroundColor: "#F1F5F9" }} data-testid="mobile-customer-sheet-notfound">
        <div className="text-slate-500 text-sm">Customer not found.</div>
        <button onClick={onBack} data-testid="mobile-customer-notfound-back" className="h-10 px-4 rounded-full bg-blue-600 text-white text-sm font-semibold">Back</button>
        <MobileBottomNav />
      </div>
    );
  }

  const isCommercial = customer.type === "Commercial";
  const isVip = Array.isArray(customer.tags) && customer.tags.includes("VIP");
  const isMember = customer.membershipInfo && customer.membershipInfo.status === "active";
  const cityLine = [customer.city, customer.state].filter(Boolean).join(", ");
  const notes = (customer.notes || []).slice().sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const photos = customer.photos || [];
  const checklists = (customer.checklists || []).slice().sort((a, b) => new Date(b.date) - new Date(a.date));
  const properties = customer.properties || [];
  const equipment = customer.equipment || [];
  const materialsUsed = customer.materialsUsed || [];
  const serviceHistory = customer.serviceHistory || [];
  const legacyHistory = customer.history || [];

  const tabCounts = {
    activity: activityItems.length,
    jobs: jobs.length,
    photos: photos.length,
    estimates: estimates.length,
    billing: invoices.length,
    checklist: checklists.length,
    history: serviceHistory.length + legacyHistory.length,
    properties: properties.length,
    equipment: equipment.length + materialsUsed.length,
    notes: notes.length,
    payments: customerPayments.length,
  };
  const recentJobs = jobs.slice().sort((a, b) => new Date(b.start || 0).getTime() - new Date(a.start || 0).getTime()).slice(0, 3);
  const sinceLabel = customer.since ? fmtDate(customer.since) : "—";
  const initials = (customer.name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

  return (
    <div className="mv md:hidden fixed inset-0 z-[60] flex flex-col" data-testid="mobile-customer-sheet">
      {/* Header */}
      <div
        className="shrink-0 relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(8,20,45,0.1), rgba(8,20,45,0.5)), url(${CUSTOMER_HEADER_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="mobile-customer-header"
      >
        <div className="relative px-3" style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.5rem)" }}>
          <div className="flex items-center justify-between">
            <button onClick={onBack} data-testid="mobile-customer-back" className="h-9 w-9 rounded-full flex items-center justify-center bg-black/25 active:bg-black/35" aria-label="Back">
              <ArrowLeft className="h-5 w-5 text-white" />
            </button>
            <div className="flex items-center gap-2">
              {canEdit && (
                <button onClick={() => setEditOpen(true)} data-testid="mobile-customer-edit" className="h-9 px-3 rounded-full flex items-center gap-1.5 bg-black/25 text-white text-[12.5px] font-semibold active:bg-black/35">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
              )}
              <button onClick={() => setHeaderMenuOpen(true)} data-testid="mobile-customer-menu" className="h-9 w-9 rounded-full flex items-center justify-center bg-black/25 text-white active:bg-black/35" aria-label="More options">
                <MoreHorizontal className="h-4.5 w-4.5" />
              </button>
            </div>
          </div>

          <div className="mt-3 pb-5 pr-20 flex items-start gap-3">
            <div className="h-16 w-16 rounded-full bg-white/20 border-2 border-white/70 flex items-center justify-center text-white text-[19px] font-bold shrink-0 backdrop-blur-sm" data-testid="mobile-customer-avatar">
              {initials || <User className="h-7 w-7" />}
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="text-[19px] font-bold text-white leading-tight truncate flex items-center gap-1.5" data-testid="mobile-customer-name">
                {customer.name || "Unnamed"}
                {isVip && <Star className="h-4 w-4 text-amber-400 fill-amber-400 shrink-0" />}
              </div>
              <div className="text-[12.5px] text-white/85 mt-0.5 truncate">
                {customer.type || "Residential"}{cityLine ? ` · ${cityLine}` : ""}
              </div>
              {customer.address && (
                <div className="mt-1 flex items-start gap-1 text-[11.5px] text-white/90">
                  <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span className="truncate">{customer.address}{cityLine ? `, ${cityLine}` : ""}</span>
                </div>
              )}
              <div className="mt-1.5 inline-flex items-center gap-1 bg-blue-600 text-white text-[10.5px] font-semibold px-2.5 py-1 rounded-full" data-testid="mobile-customer-since-pill">
                Customer Since {sinceLabel}
              </div>
              {isMember && (
                <div className="mt-1.5">
                  <span className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wide text-amber-800 bg-amber-100 rounded-full px-2 py-0.5">
                    <Crown className="h-3 w-3" /> {customer.membershipInfo.planName || "Member"}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mascot + speech bubble */}
        <div className="absolute right-2 bottom-1.5 flex items-end gap-1 pointer-events-none select-none">
          <span className="text-right text-white text-[12.5px] italic leading-tight pb-6" style={{ fontFamily: "'Dancing Script', cursive" }}>
            Great<br />Customer!
          </span>
          <img src={CUSTOMER_HEADER_MASCOT} alt="" className="h-[64px] w-auto object-contain drop-shadow-lg" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" style={{ paddingBottom: "calc(5rem + env(safe-area-inset-bottom))" }}>
        {/* Action buttons row */}
        <div className="mx-3 mt-3 grid grid-cols-5 gap-2" data-testid="mobile-customer-actions">
          <QuickAction icon={Phone} label="Call" color="#10B981" testid="mobile-customer-call" onClick={() => {
            if (!customer.phone) return toast.info("No phone on file");
            window.location.href = `tel:${customer.phone.replace(/[^0-9]/g, "")}`;
          }} />
          <QuickAction icon={Mail} label="Email" color="#2563EB" testid="mobile-customer-email" onClick={() => {
            if (!customer.email) return toast.info("No email on file");
            window.location.href = `mailto:${customer.email}`;
          }} />
          <QuickAction icon={Navigation} label="Directions" color="#7C3AED" testid="mobile-customer-directions" onClick={() => {
            if (!customer.address) return toast.info("No address on file");
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${customer.address} ${cityLine}`)}`, "_blank");
          }} />
          <QuickAction icon={MessageCircle} label="Text" color="#F97316" testid="mobile-customer-text" onClick={() => {
            if (!customer.phone) return toast.info("No phone on file");
            window.location.href = `sms:${customer.phone.replace(/[^0-9]/g, "")}`;
          }} />
          <QuickAction icon={MoreHorizontal} label="More" color="#334155" testid="mobile-customer-more-action" onClick={() => setHeaderMenuOpen(true)} />
        </div>

        {/* Tabs */}
        <div className="mx-3 mt-3 flex gap-1.5 overflow-x-auto no-scrollbar" data-testid="mobile-customer-tabs">
          {PRIMARY_TABS.map((t) => {
            const Icon = t.icon;
            const count = tabCounts[t.k];
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`mobile-customer-tab-${t.k}`}
                className={`flex items-center gap-1.5 px-3 h-9 rounded-full text-[12.5px] font-semibold whitespace-nowrap transition-colors ${
                  tab === t.k ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {t.label}
                {typeof count === "number" && count > 0 && (
                  <span className={`text-[10px] font-bold px-1.5 rounded-full ${tab === t.k ? "bg-white/20" : "bg-slate-200"}`}>{count}</span>
                )}
              </button>
            );
          })}
          <button
            onClick={() => setMoreSheetOpen(true)}
            data-testid="mobile-customer-tab-more"
            className={`flex items-center gap-1.5 px-3 h-9 rounded-full text-[12.5px] font-semibold whitespace-nowrap transition-colors ${
              MORE_TABS.some((t) => t.k === tab) ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"
            }`}
          >
            <LayoutGrid className="h-3.5 w-3.5" /> More
          </button>
        </div>

        {/* Tab content */}
        <div className="mx-3 mt-3">
          {tab === "overview" && (
            <div className="space-y-3" data-testid="mobile-customer-overview">
              {/* Stats row */}
              <div className="grid grid-cols-4 gap-2">
                <StatCard icon={DollarSign} color="#10B981" bg="#ECFDF5" value={typeof customer.lifetimeValue === "number" ? `$${customer.lifetimeValue.toLocaleString()}` : "—"} label="Lifetime Value" testid="mobile-customer-stat-lifetime" />
                <StatCard icon={FileText} color="#EF4444" bg="#FDF1F3" value={typeof customer.openBalance === "number" ? `$${customer.openBalance.toLocaleString()}` : "—"} label="Open Balance" testid="mobile-customer-stat-balance" />
                <StatCard icon={Calendar} color="#2563EB" bg="#EFF6FF" value={String(jobs.length)} label="Total Jobs" testid="mobile-customer-stat-jobs" />
                <StatCard icon={Star} color="#D97706" bg="#FFFBEB" value={customer.rating ? Number(customer.rating).toFixed(1) : "—"} label="Customer Rating" testid="mobile-customer-stat-rating" />
              </div>

              {/* Contact Information */}
              <div className="rounded-2xl bg-white shadow-sm p-4" data-testid="mobile-customer-contact-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="h-4.5 w-4.5 text-slate-700" />
                    <span className="text-[14.5px] font-bold text-slate-900">Contact Information</span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 rounded-full px-2.5 py-1">
                    {isCommercial ? <Building2 className="h-3.5 w-3.5" /> : <Home className="h-3.5 w-3.5" />} {customer.type || "Residential"}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <ContactField icon={Phone} color="#10B981" value={customer.phone || "—"} label="Mobile" testid="mobile-customer-field-phone" />
                  <ContactField icon={MapPin} color="#2563EB" value={customer.address || "—"} label={cityLine || "Address"} testid="mobile-customer-field-address" />
                  <ContactField icon={Mail} color="#2563EB" value={customer.email || "—"} label="Email" testid="mobile-customer-field-email" />
                  <ContactField icon={Calendar} color="#2563EB" value={sinceLabel} label="Customer Since" testid="mobile-customer-field-since" />
                </div>
              </div>

              {/* Recent Jobs */}
              <div className="rounded-2xl bg-white shadow-sm p-4" data-testid="mobile-customer-recent-jobs-card">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4.5 w-4.5 text-slate-700" />
                    <span className="text-[14.5px] font-bold text-slate-900">Recent Jobs</span>
                  </div>
                  <button onClick={() => setTab("jobs")} data-testid="mobile-customer-jobs-viewall" className="text-[12.5px] font-semibold text-blue-600">View All</button>
                </div>
                {recentJobs.length === 0 ? (
                  <div className="py-6 text-center text-[12.5px] text-slate-400">No jobs yet for this customer.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {recentJobs.map((j, idx) => {
                      const style = JOB_ICON_STYLES[idx % JOB_ICON_STYLES.length];
                      const JIcon = style.icon;
                      return (
                        <button
                          key={j.id}
                          onClick={() => navigate(`/mobile/jobs/${j.id}`)}
                          data-testid={`mobile-customer-recent-job-${j.id}`}
                          className="w-full text-left py-2.5 flex items-center gap-3 active:bg-slate-50"
                        >
                          <span className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${style.bg}`}>
                            <JIcon className="h-4 w-4" style={{ color: style.color }} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-[13.5px] font-bold text-slate-900 truncate">{j.jobType || j.type || "Service"} <span className="font-normal text-slate-400">#{j.id}</span></div>
                            <div className="text-[11.5px] text-slate-500">{j.status || "Scheduled"} · {j.start ? fmtDate(j.start) : "Unscheduled"}</div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Equipment / Notes preview cards */}
              <div className="grid grid-cols-2 gap-3">
                <PreviewLinkCard icon={Snowflake} title="Equipment" subtitle="View HVAC equipment, models and service history" onClick={() => setTab("equipment")} testid="mobile-customer-preview-equipment" />
                <PreviewLinkCard icon={FileText} title="Notes" subtitle="Internal notes, customer preferences and reminders" onClick={() => setTab("notes")} testid="mobile-customer-preview-notes" />
              </div>
            </div>
          )}

          {tab === "activity" && (
            activityItems.length === 0 ? (
              <EmptyState icon={ActivityIcon} text="No activity yet." />
            ) : (
              <div className="space-y-2" data-testid="mobile-customer-activity-list">
                {activityItems.map((a) => {
                  const meta = ACTIVITY_META[a.type] || { color: "bg-slate-100 text-slate-500", Icon: ActivityIcon };
                  const Icon = meta.Icon;
                  const clickable = typeof a.onClick === "function";
                  const RowTag = clickable ? "button" : "div";
                  return (
                    <RowTag
                      key={a.id}
                      onClick={clickable ? a.onClick : undefined}
                      data-testid={`mobile-customer-activity-${a.id}`}
                      className="w-full text-left rounded-xl bg-white border border-slate-200 p-3 flex items-start gap-2.5 active:bg-slate-50"
                    >
                      <span className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${meta.color}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className={`text-[13px] font-medium ${clickable ? "text-blue-700" : "text-slate-800"}`}>{a.message}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          {formatStamp(a.createdAt)}{a.by && a.by !== "system" ? ` · ${a.by}` : ""}
                        </div>
                      </div>
                    </RowTag>
                  );
                })}
              </div>
            )
          )}

          {tab === "jobs" && (
            jobs.length === 0 ? (
              <EmptyState icon={Briefcase} text="No jobs yet for this customer." />
            ) : (
              <div className="space-y-2" data-testid="mobile-customer-jobs-list">
                {jobs.slice().sort((a, b) => new Date(b.start || 0).getTime() - new Date(a.start || 0).getTime()).map((j) => (
                  <button
                    key={j.id}
                    onClick={() => navigate(`/mobile/jobs/${j.id}`)}
                    data-testid={`mobile-customer-job-${j.id}`}
                    className="w-full text-left rounded-xl bg-white border border-slate-200 p-3 flex items-center gap-2 active:bg-slate-50"
                  >
                    <Briefcase className="h-4 w-4 text-slate-400 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] font-semibold truncate text-slate-900">{j.jobType || j.type || "Service"} · #{j.id}</div>
                      <div className="text-[12px] text-slate-500">{j.status || "Scheduled"} · {j.start ? fmtDate(j.start) : "Unscheduled"}</div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
                  </button>
                ))}
              </div>
            )
          )}

          {tab === "photos" && (
            <div data-testid="mobile-customer-photos-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                  Photos{photos.length > 0 ? ` · ${photos.length}` : ""}
                </div>
                <PhotoCaptureButton onFiles={uploadPhotos} busy={photoBusy} label="Add photo" testid="mobile-customer-photo-add" menuAlign="right" />
              </div>
              {photos.length === 0 ? (
                <EmptyState icon={ImageIcon} text="No photos yet. Use “Add photo” to take a picture or upload from your gallery." />
              ) : (
                <div className="grid grid-cols-3 gap-2" data-testid="mobile-customer-photos-grid">
                  {photos.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setLightbox(p)}
                      data-testid={`mobile-customer-photo-${p.id}`}
                      className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-100"
                    >
                      <img src={fileUrl(p.url)} alt={p.caption || p.filename || "Customer photo"} loading="lazy" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "estimates" && (
            estimates.length === 0 ? (
              <EmptyState icon={FileText} text="No estimates yet." />
            ) : (
              <div className="space-y-2" data-testid="mobile-customer-estimates-list">
                {estimates.slice().sort((a, b) => new Date(b.issuedAt || 0).getTime() - new Date(a.issuedAt || 0).getTime()).map((e) => (
                  <DocRow key={e.id} kind="Estimate" doc={e} onClick={() => navigate(`/estimates?id=${e.id}`)} />
                ))}
              </div>
            )
          )}

          {tab === "billing" && (
            invoices.length === 0 ? (
              <EmptyState icon={FileText} text="No invoices yet." />
            ) : (
              <div className="space-y-2" data-testid="mobile-customer-billing-list">
                {invoices.slice().sort((a, b) => new Date(b.issuedAt || 0).getTime() - new Date(a.issuedAt || 0).getTime()).map((i) => (
                  <DocRow key={i.id} kind="Invoice" doc={i} onClick={() => navigate(`/estimates?id=${i.id}`)} />
                ))}
              </div>
            )
          )}

          {tab === "payments" && (
            <div className="space-y-4" data-testid="mobile-customer-payments-panel">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Payment History</div>
                {customerPayments.length === 0 ? (
                  <EmptyState icon={DollarSign} text="No payments recorded yet." />
                ) : (
                  <div className="space-y-2" data-testid="mobile-customer-payment-history-list">
                    {customerPayments.map((p) => (
                      <div key={p.id} className="rounded-xl bg-white border border-slate-200 p-3" data-testid={`mobile-customer-payment-${p.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[15px] font-bold text-emerald-700">${Number(p.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          <div className="text-[11px] text-slate-500 shrink-0">{formatStamp(p.receivedAt)}</div>
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px]">
                          {p.jobId && (
                            <button onClick={() => navigate(`/mobile/jobs/${p.jobId}`)} data-testid={`mobile-customer-payment-job-${p.id}`} className="text-blue-600 font-semibold">{p.jobId}</button>
                          )}
                          {p.estimate && (
                            <button onClick={() => navigate(`/estimates?id=${p.estimate.id}`)} data-testid={`mobile-customer-payment-estimate-${p.id}`} className="text-blue-600 font-semibold">{p.estimate.id}</button>
                          )}
                          <button onClick={() => navigate(`/estimates?id=${p.invoiceId}`)} data-testid={`mobile-customer-payment-invoice-${p.id}`} className="text-blue-600 font-semibold">{p.invoiceId}</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Open Balances</div>
                {customerOpenBalances.length === 0 ? (
                  <EmptyState icon={DollarSign} text="No open balances — all paid up." />
                ) : (
                  <div className="space-y-2" data-testid="mobile-customer-open-balances-list">
                    {customerOpenBalances.map((b) => (
                      <div key={b.invoiceId} className="rounded-xl bg-white border border-slate-200 p-3" data-testid={`mobile-customer-open-balance-${b.invoiceId}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[16px] font-bold text-amber-700">${Number(b.balanceDue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          <button
                            onClick={() => setEmailTarget(b.invoice)}
                            data-testid={`mobile-customer-open-balance-send-${b.invoiceId}`}
                            className="h-8 px-3 rounded-full bg-blue-600 text-white text-[12px] font-semibold flex items-center gap-1.5 shrink-0 active:scale-95 transition-transform"
                          >
                            <Send className="h-3.5 w-3.5" /> Send
                          </button>
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px]">
                          {b.jobId && (
                            <button onClick={() => navigate(`/mobile/jobs/${b.jobId}`)} data-testid={`mobile-customer-open-balance-job-${b.invoiceId}`} className="text-blue-600 font-semibold">{b.jobId}</button>
                          )}
                          {b.estimate && (
                            <button onClick={() => navigate(`/estimates?id=${b.estimate.id}`)} data-testid={`mobile-customer-open-balance-estimate-${b.invoiceId}`} className="text-blue-600 font-semibold">{b.estimate.id}</button>
                          )}
                          <button onClick={() => navigate(`/estimates?id=${b.invoiceId}`)} data-testid={`mobile-customer-open-balance-invoice-${b.invoiceId}`} className="text-blue-600 font-semibold">{b.invoiceId}</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "checklist" && (
            checklists.length === 0 ? (
              <EmptyState icon={ClipboardCheck} text="No checklists yet. Completed job-completion checklists from the field will appear here." />
            ) : (
              <div className="space-y-2.5" data-testid="mobile-customer-checklist-list">
                {checklists.map((cl) => (
                  <div key={cl.id} className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid={`mobile-customer-checklist-${cl.id}`}>
                    <div className="px-3 py-2.5 bg-slate-50 border-b border-slate-100">
                      <div className="text-[13px] font-bold text-slate-900 flex items-center gap-1.5">
                        <ClipboardCheck className="h-3.5 w-3.5 text-blue-600 shrink-0" /> {cl.flowName || "Checklist"}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {cl.jobTitle ? `${cl.jobTitle} · ` : ""}<span className="font-mono">{cl.jobId}</span>
                      </div>
                      <div className="text-[10.5px] text-slate-400 mt-0.5">{formatStamp(cl.date)}{cl.by ? ` · by ${cl.by}` : ""}</div>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {(cl.answers || []).map((a, i) => {
                        const Icon = ANSWER_ICON[a.type] || HelpCircle;
                        return (
                          <div key={`${cl.id}-${i}`} className="px-3 py-2 flex items-start gap-2">
                            <span className="h-6 w-6 rounded-md bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                              <Icon className="h-3.5 w-3.5 text-slate-500" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="text-[12px] font-semibold text-slate-700">{a.question}</div>
                              <div className="text-[12.5px] text-slate-900 mt-0.5 break-words">{a.answer || <span className="italic text-slate-400">—</span>}</div>
                            </div>
                          </div>
                        );
                      })}
                      {(cl.answers || []).length === 0 && (
                        <div className="px-3 py-2.5 text-[12px] text-slate-400 italic">No recorded answers.</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {tab === "history" && (
            (serviceHistory.length === 0 && legacyHistory.length === 0) ? (
              <EmptyState icon={HistoryIcon} text="No service history yet." />
            ) : (
              <div className="space-y-2" data-testid="mobile-customer-history-list">
                {serviceHistory.map((h) => {
                  const sc = customerStatusColors[h.status] || customerStatusColors.Completed;
                  const when = h.completedAt || h.paymentDate || h.date;
                  return (
                    <div key={h.id} data-testid={`mobile-customer-service-history-${h.id}`} className="rounded-xl bg-white border border-slate-200 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-semibold text-slate-900 flex items-center gap-1.5 flex-wrap">
                            {h.jobId ? (
                              <button onClick={() => navigate(`/mobile/jobs/${h.jobId}`)} data-testid={`mobile-customer-history-job-${h.jobId}`} className="text-blue-600">{h.jobId}</button>
                            ) : (
                              <span>{h.invoiceNumber || h.invoiceId}</span>
                            )}
                            <span className="inline-flex text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: sc.bg, color: sc.text }}>{h.status}</span>
                          </div>
                          <div className="text-[11.5px] text-slate-600">{h.type}</div>
                          <div className="text-[10.5px] text-slate-500 mt-0.5">
                            {when && fmtDate(when)}{h.tech && h.tech !== "—" ? ` · ${h.tech}` : ""}
                          </div>
                          {h.notes && <div className="mt-1 text-[11.5px] text-slate-600 whitespace-pre-wrap">{h.notes}</div>}
                        </div>
                        <div className="font-bold text-emerald-700 text-[13px] shrink-0">${(h.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                    </div>
                  );
                })}
                {legacyHistory.map((h) => {
                  const sc = customerStatusColors[h.status] || customerStatusColors.Completed;
                  return (
                    <div key={h.id} className="rounded-xl bg-white border border-slate-200 p-3 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-slate-900 flex items-center gap-1.5">
                          {h.id}
                          <span className="inline-flex text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: sc.bg, color: sc.text }}>{h.status}</span>
                        </div>
                        <div className="text-[11.5px] text-slate-600">{h.type}</div>
                        <div className="text-[10.5px] text-slate-500">{fmtDate(h.date)} · {h.tech}</div>
                      </div>
                      <div className="font-bold text-emerald-700 text-[13px] shrink-0">${h.value.toLocaleString()}</div>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {tab === "properties" && (
            properties.length === 0 ? (
              <EmptyState icon={Building2} text="No properties on file." />
            ) : (
              <div className="space-y-2" data-testid="mobile-customer-properties-list">
                {properties.map((p) => (
                  <div key={p.id} className="rounded-xl bg-white border border-slate-200 p-3 flex items-start gap-2">
                    <Building2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-slate-900">{p.label}</div>
                      <div className="text-[11.5px] text-slate-500">{p.address}</div>
                      <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-600">
                        <span>{(p.sqft || 0).toLocaleString()} sq ft</span>
                        <span>·</span>
                        <span>{p.units} unit{p.units !== 1 ? "s" : ""}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {tab === "equipment" && (
            (equipment.length === 0 && materialsUsed.length === 0) ? (
              <EmptyState icon={Wrench} text="No equipment on file." />
            ) : (
              <div className="space-y-4" data-testid="mobile-customer-equipment-list">
                {equipment.length > 0 && (
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Serviced Equipment</div>
                    <div className="space-y-2">
                      {equipment.map((e) => (
                        <div key={e.id} data-testid={`mobile-customer-equipment-${e.id}`} className="rounded-xl bg-white border border-slate-200 p-3 flex items-start gap-2">
                          <Wrench className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-semibold text-slate-900">{e.type}</div>
                            {(e.brand || e.model) && <div className="text-[11.5px] text-slate-600">{e.brand} {e.model}</div>}
                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
                              {e.serial && <span className="font-mono">SN: {e.serial}</span>}
                              {e.installedAt && <span>Installed {fmtDate(e.installedAt)}</span>}
                              {e.warranty && <span className={`font-semibold ${e.warranty === "Expired" ? "text-red-600" : "text-emerald-700"}`}>Warranty: {e.warranty}</span>}
                              {e.jobId && (
                                <button onClick={() => navigate(`/mobile/jobs/${e.jobId}`)} className="text-blue-600 font-semibold" data-testid={`mobile-customer-equipment-job-${e.jobId}`}>{e.jobId}</button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {materialsUsed.length > 0 && (
                  <div data-testid="mobile-customer-materials-used">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Parts &amp; Materials History</div>
                    <div className="space-y-2">
                      {materialsUsed.map((m) => (
                        <div key={m.id} data-testid={`mobile-customer-material-${m.id}`} className="rounded-xl bg-white border border-slate-200 p-3 flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-semibold text-slate-900 truncate">{m.name}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
                              <span>{m.qty} {m.unit || "each"}</span>
                              {m.sku && <span className="font-mono">SKU {m.sku}</span>}
                              {m.usedAt && <span>{fmtDate(m.usedAt)}</span>}
                              {m.jobId && (
                                <button onClick={() => navigate(`/mobile/jobs/${m.jobId}`)} className="text-blue-600 font-semibold" data-testid={`mobile-customer-material-job-${m.jobId}`}>{m.jobId}</button>
                              )}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[13px] font-bold text-slate-900">${((m.qty || 0) * (m.unitPrice || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          )}

          {tab === "notes" && (
            <div data-testid="mobile-customer-notes">
              <div className="rounded-2xl bg-white border border-slate-200 p-3">
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add a note about this customer…"
                  rows={3}
                  data-testid="mobile-customer-note-input"
                  className="w-full text-[13.5px] resize-none focus:outline-none"
                />
                <div className="flex justify-end mt-1">
                  <button
                    onClick={addNote}
                    disabled={!noteText.trim() || noteBusy}
                    data-testid="mobile-customer-note-submit"
                    className="h-9 px-3 rounded-full bg-blue-600 text-white text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {noteBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Save
                  </button>
                </div>
              </div>

              {notes.length === 0 ? (
                <EmptyState icon={StickyNote} text="No notes yet." />
              ) : (
                <div className="mt-2 space-y-2">
                  {notes.map((n) => {
                    const canModify = isAdmin || n.createdBy === myEmail;
                    const editing = editingNoteId === n.id;
                    return (
                      <div key={n.id} className="rounded-xl bg-white border border-slate-200 p-3" data-testid={`mobile-customer-note-${n.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[11px] text-slate-400">{formatStamp(n.createdAt)} · {n.createdBy || "—"}{n.updatedAt ? " (edited)" : ""}</div>
                          {canModify && !editing && (
                            <div className="flex items-center gap-1 shrink-0">
                              <button onClick={() => beginEditNote(n)} data-testid={`mobile-customer-note-edit-${n.id}`} className="h-7 w-7 rounded flex items-center justify-center text-slate-500 active:bg-slate-100">
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={() => removeNote(n.id)} data-testid={`mobile-customer-note-delete-${n.id}`} className="h-7 w-7 rounded flex items-center justify-center text-slate-500 active:bg-red-50">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                        {editing ? (
                          <div className="mt-1.5 space-y-1.5">
                            <textarea
                              rows={3}
                              value={editingNoteText}
                              onChange={(e) => setEditingNoteText(e.target.value)}
                              data-testid={`mobile-customer-note-edit-textarea-${n.id}`}
                              className="w-full px-2 py-1.5 rounded border border-blue-300 text-[13px] focus:outline-none resize-none"
                              autoFocus
                            />
                            <div className="flex justify-end gap-1.5">
                              <button onClick={cancelEditNote} className="h-8 px-3 rounded-full border border-slate-200 text-[12px] font-semibold text-slate-600">Cancel</button>
                              <button onClick={saveEditNote} disabled={!editingNoteText.trim() || noteBusy} data-testid={`mobile-customer-note-edit-save-${n.id}`} className="h-8 px-3 rounded-full bg-emerald-600 text-white text-[12px] font-semibold flex items-center gap-1 disabled:opacity-50">
                                <Check className="h-3.5 w-3.5" /> Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-1 text-[13px] text-slate-800 whitespace-pre-wrap">{n.text}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Photo lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 p-4"
          onClick={() => setLightbox(null)}
          data-testid="mobile-customer-photo-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            data-testid="mobile-customer-photo-lightbox-close"
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/15 text-white flex items-center justify-center"
          >
            <X className="h-5 w-5" />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-md w-full">
            <img
              src={fileUrl(lightbox.url)}
              alt={lightbox.caption || lightbox.filename || "Customer photo"}
              className="w-full max-h-[70vh] object-contain rounded-lg shadow-2xl"
            />
            <div className="mt-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-white text-[13px] font-medium truncate">{lightbox.caption || lightbox.filename}</div>
                <div className="text-white/50 text-[11px]">{(lightbox.createdAt || "").slice(0, 10)}{lightbox.uploadedBy ? ` · ${lightbox.uploadedBy}` : ""}</div>
              </div>
              {canEdit && (
                <button
                  onClick={() => deletePhoto(lightbox.id)}
                  data-testid="mobile-customer-photo-delete"
                  className="h-9 px-3 rounded-full text-[13px] bg-white/10 border border-white/20 text-white shrink-0 flex items-center gap-1.5"
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* More tabs bottom sheet */}
      {moreSheetOpen && (
        <>
          <div className="fixed inset-0 z-[75] bg-black/40" onClick={() => setMoreSheetOpen(false)} data-testid="mobile-customer-more-sheet-backdrop" />
          <div className="fixed left-0 right-0 bottom-0 z-[76] bg-white rounded-t-2xl shadow-2xl p-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)]" data-testid="mobile-customer-more-sheet">
            <div className="h-1 w-10 rounded-full bg-slate-200 mx-auto my-2" />
            {MORE_TABS.map((t) => {
              const Icon = t.icon;
              const count = tabCounts[t.k];
              return (
                <button
                  key={t.k}
                  onClick={() => { setTab(t.k); setMoreSheetOpen(false); }}
                  data-testid={`mobile-customer-more-item-${t.k}`}
                  className="w-full flex items-center justify-between px-4 py-3 text-[14.5px] font-semibold text-slate-700 active:bg-slate-50 rounded-xl"
                >
                  <span className="flex items-center gap-3"><Icon className="h-4.5 w-4.5 text-slate-500" /> {t.label}</span>
                  {typeof count === "number" && count > 0 && <span className="text-[11px] font-bold px-1.5 rounded-full bg-slate-100 text-slate-500">{count}</span>}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Header 3-dot menu sheet */}
      {headerMenuOpen && (
        <>
          <div className="fixed inset-0 z-[75] bg-black/40" onClick={() => setHeaderMenuOpen(false)} data-testid="mobile-customer-header-menu-backdrop" />
          <div className="fixed left-0 right-0 bottom-0 z-[76] bg-white rounded-t-2xl shadow-2xl p-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)]" data-testid="mobile-customer-header-menu">
            <div className="h-1 w-10 rounded-full bg-slate-200 mx-auto my-2" />
            {canEdit && (
              <button onClick={() => { setHeaderMenuOpen(false); setEditOpen(true); }} data-testid="mobile-customer-menu-edit" className="w-full flex items-center gap-3 px-4 py-3 text-[14.5px] font-semibold text-slate-700 active:bg-slate-50 rounded-xl">
                <Pencil className="h-4.5 w-4.5 text-slate-500" /> Edit Customer
              </button>
            )}
            {canEdit && (
              <button onClick={deleteCustomer} data-testid="mobile-customer-menu-delete" className="w-full flex items-center gap-3 px-4 py-3 text-[14.5px] font-semibold text-red-600 active:bg-red-50 rounded-xl">
                <Trash2 className="h-4.5 w-4.5" /> Delete Customer
              </button>
            )}
            <button onClick={() => setHeaderMenuOpen(false)} data-testid="mobile-customer-menu-cancel" className="w-full flex items-center justify-center gap-2 px-4 py-3 text-[14.5px] font-semibold text-slate-400 active:bg-slate-50 rounded-xl">
              <X className="h-4.5 w-4.5" /> Cancel
            </button>
          </div>
        </>
      )}

      {editOpen && (
        <NewCustomerModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          editCustomer={customer}
          onSaved={(saved) => { setCustomer(saved); setEditOpen(false); toast.success("Customer updated"); }}
        />
      )}

      {emailTarget && (
        <EmailDocModal
          kind="invoice"
          doc={emailTarget}
          customer={customer}
          payLink={`${window.location.origin}/pay/${emailTarget.id}`}
          onClose={() => setEmailTarget(null)}
          onSent={load}
        />
      )}

      <NewJobModal
        open={newJobOpen}
        prefillCustomer={customer}
        onClose={() => setNewJobOpen(false)}
        onCreated={(job) => {
          setNewJobOpen(false);
          toast.success(`Job ${job?.id || ""} created`);
          load();
          if (job?.id) navigate(`/mobile/jobs/${job.id}`);
        }}
      />

      {estimateOpen && (
        <CreateDialog
          kind="estimate"
          customers={[customer]}
          jobs={jobs}
          prefillCustomerId={customer.id}
          onClose={() => setEstimateOpen(false)}
          onCreated={(doc) => {
            setEstimateOpen(false);
            toast.success(`Estimate ${doc?.id || ""} created`);
            if (doc?.id) navigate(`/estimates?id=${doc.id}`);
          }}
        />
      )}

      {canEdit && (
        <button
          onClick={() => setEstimateOpen(true)}
          data-testid="mobile-customer-new-estimate-btn"
          className="fixed left-4 z-[55] h-12 pl-4 pr-5 rounded-full bg-blue-600 text-white shadow-lg flex items-center gap-2 font-semibold text-[13.5px] active:scale-95 transition-transform"
          style={{ bottom: "calc(95px + env(safe-area-inset-bottom))" }}
        >
          <FileText className="h-4.5 w-4.5" /> Estimate
        </button>
      )}

      {canEdit && (
        <button
          onClick={() => setNewJobOpen(true)}
          data-testid="mobile-customer-new-job-btn"
          className="fixed right-4 z-[55] h-12 pl-4 pr-5 rounded-full bg-blue-600 text-white shadow-lg flex items-center gap-2 font-semibold text-[13.5px] active:scale-95 transition-transform"
          style={{ bottom: "calc(95px + env(safe-area-inset-bottom))" }}
        >
          <Plus className="h-4.5 w-4.5" /> New Job
        </button>
      )}

      <MobileBottomNav />
    </div>
  );
}

function QuickAction({ icon: Icon, label, color, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="aspect-square bg-white rounded-2xl shadow-sm flex flex-col items-center justify-center gap-1.5 active:scale-95 transition-transform">
      <Icon className="h-5 w-5" style={{ color }} />
      <span className="text-[10.5px] font-semibold text-slate-700">{label}</span>
    </button>
  );
}

function StatCard({ icon: Icon, color, bg, value, label, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl bg-white shadow-sm p-3 flex flex-col items-center text-center gap-1.5" style={bg ? { backgroundColor: bg } : undefined}>
      <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}1A` }}>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <div className="text-[15px] font-bold text-slate-900 leading-tight truncate w-full">{value}</div>
      <div className="text-[10px] text-slate-500 leading-tight">{label}</div>
    </div>
  );
}

function ContactField({ icon: Icon, color, value, label, testid }) {
  return (
    <div data-testid={testid} className="flex items-start gap-2">
      <div className="h-7 w-7 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: `${color}1A` }}>
        <Icon className="h-3.5 w-3.5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-slate-800 truncate">{value}</div>
        <div className="text-[11px] text-slate-400">{label}</div>
      </div>
    </div>
  );
}

function PreviewLinkCard({ icon: Icon, title, subtitle, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="rounded-2xl bg-white shadow-sm p-4 text-left active:bg-slate-50">
      <div className="flex items-center justify-between">
        <Icon className="h-5 w-5 text-blue-600" />
        <ChevronRight className="h-4 w-4 text-slate-300" />
      </div>
      <div className="mt-2 text-[13.5px] font-bold text-slate-900">{title}</div>
      <div className="mt-0.5 text-[11px] text-slate-500 leading-snug">{subtitle}</div>
    </button>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5">
      <span className="text-[12.5px] text-slate-500">{label}</span>
      <span className="text-[13px] font-semibold text-slate-800 text-right truncate max-w-[60%]">{value}</span>
    </div>
  );
}

function DocRow({ kind, doc, onClick }) {
  const total = typeof doc.total === "number" ? `$${doc.total.toLocaleString()}` : "—";
  return (
    <button
      onClick={onClick}
      data-testid={`mobile-customer-doc-${doc.id}`}
      className="w-full text-left rounded-xl bg-white border border-slate-200 p-3 flex items-center gap-2 active:bg-slate-50"
    >
      <FileText className="h-4 w-4 text-slate-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold truncate text-slate-900">{kind} #{doc.id} · {total}</div>
        <div className="text-[12px] text-slate-500">{doc.status || "—"} · {fmtDate(doc.issuedAt)}</div>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
    </button>
  );
}

function EmptyState({ text, icon: Icon }) {
  return (
    <div className="text-center py-10 rounded-xl border-2 border-dashed border-slate-200 text-slate-400" data-testid="mobile-customer-tab-empty">
      {Icon && <Icon className="h-7 w-7 mx-auto mb-2 text-slate-300" />}
      <div className="text-[13px] px-4">{text}</div>
    </div>
  );
}
